import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import Tooltip from '@mui/material/Tooltip'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Divider from '@mui/material/Divider'
import { keyframes } from '@mui/material/styles'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CloseIcon from '@mui/icons-material/Close'
import TuneIcon from '@mui/icons-material/Tune'
import AddIcon from '@mui/icons-material/Add'
import { Form, Formik, FormikHelpers } from 'formik'
import { useMounted } from '@/hooks/use-mounted'
import useTranslate from '@/hooks/use-translate'
import {
  callAccountGroups,
  Configuration,
  findConfiguration,
  Group,
  PreparationMode,
} from '@/api'
import { useDefaultPreparation } from '@/hooks/use-default-preparation'
import DialogUploadValues from '@/types/dialogUploadValues'
import { MAX_DOSSIER_NAME_LENGTH } from '@/constants/NameLengths'
import { handleSubmit, useUploadFile } from '../utils'
import buildWizardValidationSchema from './utils/buildWizardValidationSchema'
import { TagsField } from '@/components/TagField'
import { useLocation, useNavigate } from 'react-router'
import type { DialogUploadProps } from '../types'
import { NfField } from './primitives/NfField'
import { NfPill } from './primitives/NfPill'
import { NfTextReveal } from './primitives/NfTextReveal'
import { getNf, nf, nfTitleFont } from './nfTokens'
import { useWizardStep, isStepValid } from './useWizardStep'
import { useAnimatedStepHeight } from './useAnimatedStepHeight'
import { WizardDefaults } from './useWizardDefaults'
import { WIZARD_LAST_STEP } from './wizardSteps'
import { FilesField } from './fields/FilesField'
import { FolderNameField } from './fields/FolderNameField'
import Alert from '@mui/material/Alert'
import { useRightCheck } from '@/components/RightGate'
import { useRights } from '@/hooks/use-rights'
import { AgencyField } from './fields/AgencyField'
import { DeclarationTypeField } from './fields/DeclarationTypeField'
import { WorkflowField } from './fields/WorkflowField'
import { ClientField } from './fields/ClientField'
import { PreparationModeField } from './fields/PreparationModeField'
import { getWizardConfigurations } from './utils/wizardGroupConfigurations'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { FeatureNames } from '@/lib/featureFlags'

const EMPTY_FILES: DialogUploadValues['files'] = []
/** Le temps de voir la coche du bouton avant que la fenêtre se ferme. */
const SUBMIT_DONE_HOLD_MS = 450

const stepEnter = (fromX: number) => keyframes`
  from { opacity: 0; transform: translateX(${fromX}px); }
  to { opacity: 1; transform: none; }
`
const stepEnterFromLeft = stepEnter(-16)
const stepEnterFromRight = stepEnter(16)

/** Le panneau qui apparaît glisse depuis le côté de l'étape qu'il occupe. `backwards` : aucun transform ne reste après. */
const stepPanelSx = (enter: ReturnType<typeof keyframes>) => ({
  animation: `${enter} 260ms cubic-bezier(.2, .8, .2, 1) backwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
})

const FolderAgencyFields: FC<{
  myGroups?: Group[]
  configurations?: Configuration[]
  groupId: string
  onManualGroupChange: () => void
}> = ({ myGroups, configurations, groupId, onManualGroupChange }) => {
  const groupConfigurations = useMemo(
    () => getWizardConfigurations(configurations, groupId, myGroups),
    [configurations, groupId, myGroups]
  )

  return (
    <>
      <AgencyField myGroups={myGroups} onManualGroupChange={onManualGroupChange} />
      <Stack direction="row" spacing={1.5}>
        <Box sx={{ flex: 1 }}>
          <DeclarationTypeField configurations={groupConfigurations} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <WorkflowField configurations={groupConfigurations} />
        </Box>
      </Stack>
    </>
  )
}

/**
 * DialogUploadWizard (TEC-1601)
 *
 * Wizard 2 étapes (Fichiers → Dossier) avec création rapide dès l’étape Fichiers.
 * Réutilise la même DialogUploadProps et le même pipeline d'envoi
 * (handleSubmit/useUploadFile) que l'ancienne modale.
 */
export const DialogUploadWizard: FC<DialogUploadProps> = (props) => {
  const {
    open,
    inputFiles = EMPTY_FILES,
    onFilesUploadCompleted = () => undefined,
    onClose,
    defaultGroupId,
    defaultFluxId,
    defaultPreparation: forcedPreparation,
  } = props

  const { isFeatureEnabled, isLoaded: featureFlagsLoaded } = useFeatureFlags()
  // Sans le flag, tout dossier part en préparation classique, quelle que soit la préférence enregistrée.
  const smartPreparationEnabled = isFeatureEnabled(
    FeatureNames.smartPreparation
  )
  const { mode: defaultPreparation, loaded: defaultPreparationLoaded } = useDefaultPreparation()
  const preparationTouched = useRef(false)
  useEffect(() => {
    if (!open) preparationTouched.current = false
  }, [open])

  const initialValues = useMemo<DialogUploadValues>(() => {
    let recordName = inputFiles[0]?.name.split('.').slice(0, -1).join('.') ?? ''
    if (recordName.length > MAX_DOSSIER_NAME_LENGTH) {
      recordName = recordName.slice(0, MAX_DOSSIER_NAME_LENGTH)
    }
    return {
      files: inputFiles.length > 0 ? inputFiles : EMPTY_FILES,
      recordName,
      exportConfiguration: '',
      group: defaultGroupId || '',
      workflow: defaultFluxId || '',
      clientOrganizationId: '',
      tagIds: [],
      agentic: false,
    }
  }, [inputFiles, defaultGroupId, defaultFluxId])

  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')
  const { t: tDashboard } = useTranslate('dashboard')

  const allowClientOrganizationInput = isFeatureEnabled(FeatureNames.clientOrganizationUpload)
  const allowNewDisplayAndTags = isFeatureEnabled(FeatureNames.newTrackingView)

  const navigate = useNavigate()
  const { pathname: currentPathname } = useLocation()
  const queryClient = useQueryClient()
  const useZip = true
  const { uploadFile } = useUploadFile(useZip)
  const validationSchema = buildWizardValidationSchema()
  const isMounted = useMounted()

  const [configurations, setConfigurations] = useState<Configuration[]>()
  const [accountGroups, setAccountGroups] = useState<Group[]>()
  // En mode actif, seules les agences où le membre peut créer un dossier (TEC-1677) ; sinon, toutes.
  const rights = useRights()
  const myGroups = useMemo(
    () => accountGroups?.filter((group) => rights.can('declarations.create', group.gid)),
    [accountGroups, rights]
  )
  const createRight = useRightCheck('declarations.create')
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false)
  const [submitDone, setSubmitDone] = useState(false)
  const groupTouchedRef = useRef(false)
  const wizardWasOpenRef = useRef(false)
  if (open && !wizardWasOpenRef.current) {
    groupTouchedRef.current = false
  }
  wizardWasOpenRef.current = open
  const markGroupTouched = useCallback(() => {
    groupTouchedRef.current = true
  }, [])

  const {
    step,
    maxStep,
    showStepErrors,
    setShowStepErrors,
    advance,
    goBack,
    reset: resetWizard,
  } = useWizardStep(inputFiles.length > 0 ? 1 : 0)
  const paperRef = useAnimatedStepHeight(step)

  const { data: { data: configurationsData } = {} } = useQuery({
    queryKey: ['findConfigurations'],
    queryFn: () => findConfiguration(),
  })

  useEffect(() => {
    setConfigurations(configurationsData)
  }, [configurationsData])

  const onSubmit = useCallback(
    (values: DialogUploadValues, helpers: FormikHelpers<DialogUploadValues>) => {
      if (!submitInProgress) {
        setSubmitInProgress(true)
        setSubmitDone(false)
        return handleSubmit({
          values,
          helpers,
          onFilesUploadCompleted,
          uploadFile,
          onClose,
          successMessage: tCommon('folderCreationInProgress'),
          isMounted,
          setSubmitInProgress,
          useZip,
          navigate,
          currentPathname,
          queryClient,
          configurations,
          onUploaded: () => {
            setSubmitDone(true)
            return new Promise((resolve) => {
              setTimeout(resolve, SUBMIT_DONE_HOLD_MS)
            })
          },
        })
      }
    },
    [
      onFilesUploadCompleted,
      onClose,
      isMounted,
      tCommon,
      useZip,
      navigate,
      currentPathname,
      queryClient,
      configurations,
      submitInProgress,
      uploadFile,
    ]
  )

  const handleClose = useCallback(
    (handleReset: () => void) => {
      handleReset()
      onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      callAccountGroups()
        .then((validGroups) => setAccountGroups(validGroups.data))
        .catch((e) => console.error('error callAccountGroups', e))
    }
  }, [open])

  useEffect(() => {
    if (open) {
      resetWizard(inputFiles.length > 0 ? 1 : 0)
      setSubmitDone(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
    >
      {({ handleReset, values, setFieldValue, setValues, errors, validateForm, submitForm }) => {
        const stepValid = isStepValid(step, errors)

        const classicFlow = !values.agentic
        const selectedAgencyName =
          myGroups?.find((group) => group.gid === values.group)?.name ?? ''
        const quickCreateTooltip =
          values.agentic && selectedAgencyName
            ? tUpload('wizardQuickCreateTooltip', { agency: selectedAgencyName })
            : ''
        const quickCreateLabel =
          values.agentic && selectedAgencyName
            ? tUpload('wizardQuickCreateButtonInAgency', { agency: selectedAgencyName })
            : tUpload('wizardQuickCreateButton')

        const goOptions = () => {
          setShowStepErrors(true)
          advance()
        }

        const handleQuickCreate = () => {
          void validateForm().then((freshErrors) => {
            setShowStepErrors(true)
            // Le nom et le type doivent être valides pour la création
            // rapide ; sinon on bascule sur les Options pour les compléter.
            if (!freshErrors.recordName && !freshErrors.exportConfiguration) {
              void submitForm()
            } else if (step === 0) {
              advance()
            }
          })
        }

        const handleFinalCreate = () => {
          void submitForm()
        }

        const subtitle = submitInProgress
          ? tUpload('wizardSubmitting')
          : step === 0
            ? !values.agentic
              ? tUpload('wizardFilesStepGuidanceClassic')
              : tUpload('wizardFilesStepGuidance')
            : tUpload('wizardStepFolderSubtitle')

        return (
          <Dialog
            open={open}
            onClose={() => handleClose(handleReset)}
            PaperProps={{
              ref: paperRef,
              sx: (theme) => ({
                width: 560,
                maxWidth: '92vw',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: `1px solid ${getNf(theme).line}`,
              }),
            }}
          >
            <Form
              data-testid="dialog-upload-form"
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && step !== WIZARD_LAST_STEP) {
                  event.preventDefault()
                }
              }}
            >
              <WizardDefaults
                myGroups={myGroups}
                configurations={configurations}
                groupTouchedRef={groupTouchedRef}
              />
              <ApplyDefaultPreparation
                open={open}
                loaded={
                  featureFlagsLoaded &&
                  (forcedPreparation ? true : defaultPreparationLoaded)
                }
                mode={
                  smartPreparationEnabled
                    ? forcedPreparation ?? defaultPreparation
                    : null
                }
                touched={preparationTouched}
                setFieldValue={setFieldValue}
              />
              <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ p: '20px 20px 16px' }}>
                <Box
                  sx={(theme) => ({
                    width: 36,
                    height: 36,
                    borderRadius: `${nf.radiusCard}px`,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    bgcolor: getNf(theme).greenTint,
                    color: 'primary.main',
                  })}
                >
                  <FolderOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={(theme) => ({
                      fontFamily: nfTitleFont,
                      fontSize: 15,
                      fontWeight: 700,
                      letterSpacing: '-.1px',
                      color: getNf(theme).ink,
                    })}
                  >
                    <NfTextReveal text={tCommon('creationOfNewFolder')} />
                  </Typography>
                  <Typography
                    sx={(theme) => ({ fontSize: 11, color: getNf(theme).muted, mt: 0.25 })}
                  >
                    {/* La clé rejoue la révélation à chaque changement d'étape ou au départ de l'envoi. */}
                    <NfTextReveal key={subtitle} text={subtitle} delay={0.12} blur={3} />
                  </Typography>
                </Box>
                <IconButton
                  aria-label={tCommon('close')}
                  onClick={() => !submitInProgress && handleClose(handleReset)}
                  size="small"
                  sx={(theme) => ({ width: 26, height: 26, color: getNf(theme).muted2 })}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Stack>

              <Divider sx={(theme) => ({ borderColor: getNf(theme).line2 })} />

              <DialogContent
                sx={{
                  p: '18px 20px 22px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: step === 0 ? 'hidden' : 'auto',
                }}
              >
                {/* Étapes visitées gardées montées (display:none) : un démontage
                    des Autocomplete Options viderait Formik, et « Création rapide »
                    perdrait ces choix. */}
                <Box
                  data-testid="wizard-panel-files"
                  sx={{
                    display: step === 0 ? 'flex' : 'none',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    ...stepPanelSx(stepEnterFromLeft),
                  }}
                >
                  <Stack spacing={3} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <FilesField useZip={useZip} />
                    {smartPreparationEnabled && (
                      <Box sx={{ flexShrink: 0 }}>
                        <PreparationModeField
                          agentic={values.agentic}
                          onChange={(checked) => {
                            preparationTouched.current = true
                            if (checked) {
                              setValues({
                                ...values,
                                agentic: true,
                                exportConfiguration: '',
                                workflow: '',
                                clientOrganizationId: '',
                                tagIds: [],
                              })
                              return
                            }
                            setFieldValue('agentic', false)
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>

                {maxStep >= 1 && (
                  <Box
                    data-testid="wizard-panel-folder"
                    sx={{
                      display: step === 1 ? 'block' : 'none',
                      ...stepPanelSx(stepEnterFromRight),
                    }}
                  >
                    <Stack spacing={1.75}>
                      {createRight.reason && (
                        <Alert severity="info">{createRight.reason}</Alert>
                      )}
                      <FolderNameField />
                      <FolderAgencyFields
                        myGroups={myGroups}
                        configurations={configurations}
                        groupId={values.group}
                        onManualGroupChange={markGroupTouched}
                      />
                      {allowClientOrganizationInput && <ClientField />}
                      {allowNewDisplayAndTags && (
                        <NfField
                          label={tDashboard('tags')}
                          tooltip={tUpload('wizardTagsHint')}
                        >
                          <TagsField
                            name="tagIds"
                            hideLabel
                            placeholder={tUpload('wizardTagsPlaceholder')}
                          />
                        </NfField>
                      )}
                    </Stack>
                  </Box>
                )}
              </DialogContent>

              <Divider sx={(theme) => ({ borderColor: getNf(theme).line2 })} />
              <DialogActions sx={{ p: '14px 20px' }}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%' }}>
                  <Typography
                    sx={(theme) => {
                      const t = getNf(theme)
                      return {
                        flex: 1,
                        fontSize: 10.5,
                        color: showStepErrors && !stepValid ? t.danger : t.muted,
                      }
                    }}
                  >
                    {submitInProgress
                      ? tUpload('wizardSubmitting')
                      : step === 0 && values.files.length === 0
                        ? tUpload('wizardAddAtLeastOneFile')
                        : showStepErrors && !stepValid
                          ? tUpload('wizardCompleteRequiredFields')
                          : ''}
                  </Typography>

                  {step > 0 && !submitInProgress && (
                    <NfPill variant="ghost" onClick={goBack} data-testid="wizard-back-button">
                      {tCommon('back')}
                    </NfPill>
                  )}

                  {step === 0 && !submitInProgress && !classicFlow && (
                    <NfPill
                      variant="secondary"
                      icon={<TuneIcon sx={{ fontSize: 14 }} />}
                      onClick={goOptions}
                      disabled={values.files.length === 0}
                      data-testid="wizard-options-button"
                    >
                      {tUpload('wizardOptionsButton')}
                    </NfPill>
                  )}

                  {step === 0 && classicFlow ? (
                    <NfPill
                      variant="primary"
                      onClick={goOptions}
                      disabled={submitInProgress || values.files.length === 0}
                      data-testid="wizard-next-button"
                    >
                      {tCommon('next')}
                    </NfPill>
                  ) : step === WIZARD_LAST_STEP ? (
                    <NfPill
                      variant="primary"
                      icon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={handleFinalCreate}
                      disabled={submitInProgress || values.files.length === 0}
                      loading={submitInProgress}
                      done={submitDone}
                      data-testid="submit-button"
                    >
                      {submitDone
                        ? tUpload('wizardCreated')
                        : submitInProgress
                          ? tUpload('wizardCreating')
                          : tUpload('wizardCreateFolderButton')}
                    </NfPill>
                  ) : (
                    <Tooltip
                      title={quickCreateTooltip}
                      arrow
                      placement="top"
                      disableHoverListener={!quickCreateTooltip}
                      disableFocusListener={!quickCreateTooltip}
                      slotProps={{ tooltip: { sx: { maxWidth: 280, fontSize: 11 } } }}
                    >
                      <span aria-label={quickCreateTooltip || undefined}>
                        <NfPill
                          variant="primary"
                          icon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={handleQuickCreate}
                          disabled={submitInProgress || values.files.length === 0}
                          loading={submitInProgress}
                          done={submitDone}
                          data-testid="wizard-quick-create-button"
                        >
                          {submitDone
                            ? tUpload('wizardCreated')
                            : submitInProgress
                              ? tUpload('wizardCreating')
                              : quickCreateLabel}
                        </NfPill>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </DialogActions>
            </Form>
          </Dialog>
        )
      }}
    </Formik>
  )
}

const ApplyDefaultPreparation: FC<{
  open: boolean
  loaded: boolean
  mode: PreparationMode | null
  touched: { current: boolean }
  setFieldValue: (field: string, value: boolean) => void
}> = ({ open, loaded, mode, touched, setFieldValue }) => {
  useEffect(() => {
    if (!open || !loaded || touched.current) return
    touched.current = true
    setFieldValue('agentic', mode === PreparationMode.Intelligent)
  }, [open, loaded, mode, setFieldValue, touched])

  return null
}

export default DialogUploadWizard
